import { normalizeCountryCode } from './countryCurrency';
import { getPathLocale, setPathLocale, SITE_LANGUAGES, stripPathLocale } from './localeShared';
import { IXsollaPublicMarketConfig, IXsollaPublicSiteConfig, TXsollaCheckoutMethod } from './xsolla';

export type TXsollaRolloutConfig = IXsollaPublicSiteConfig;

export type TXsollaCheckoutExperience = {
  isVisible: boolean;
  displayLocale: string;
  language: string;
  marketCountry: string | null;
  sandbox: boolean;
  availablePaymentMethods: TXsollaCheckoutMethod[];
};

const XSOLLA_ROLLOUT_BUCKET_MULTIPLIER = 0xcea7 % 100;

/**
 * The one market that is public ahead of the phased rollout, and the only market affinity is
 * allowed to move a buyer into.
 */
export const XSOLLA_PILOT_MARKET_COUNTRY = 'CN';

function getXsollaPathLocale(pathname: string): string {
  return getPathLocale(pathname) || '';
}

export function isXsollaPilotLocale(pathname: string): boolean {
  return getXsollaPathLocale(pathname) === 'cn';
}

function getMarketConfig(
  countryCode: string | null | undefined,
  config?: TXsollaRolloutConfig | null
): IXsollaPublicMarketConfig | null {
  const normalizedCountryCode = normalizeCountryCode(countryCode);
  if (!normalizedCountryCode || !config?.markets) {
    return null;
  }

  return config.markets[normalizedCountryCode] || null;
}

function pickBypassMarketGatingMarket(
  pathname: string,
  config?: TXsollaRolloutConfig | null,
  trustedCountryCode?: string | null
): string | null {
  const normalizedTrustedCountry = normalizeCountryCode(trustedCountryCode);
  if (normalizedTrustedCountry && getMarketConfig(normalizedTrustedCountry, config)) {
    return normalizedTrustedCountry;
  }

  const pathLocale = getXsollaPathLocale(pathname);
  const localeMatchedMarket = Object.entries(config?.markets || {}).find(([, market]) => market.siteLocales.includes(pathLocale));
  if (localeMatchedMarket) {
    return localeMatchedMarket[0];
  }

  return Object.keys(config?.markets || {})[0] || null;
}

function isPathAllowedForMarket(pathname: string, market: IXsollaPublicMarketConfig | null): boolean {
  if (!market) {
    return false;
  }

  const pathLocale = getXsollaPathLocale(pathname);
  if (market.siteLocales.includes(pathLocale)) {
    return true;
  }

  return market.defaultForCountry && isXsollaPremiumPath(pathname);
}

function isXsollaPremiumPath(pathname: string): boolean {
  const cleanPathname = (pathname || '/').split(/[?#]/)[0] || '/';
  const unlocalizedPathname = stripPathLocale(cleanPathname);

  return unlocalizedPathname === '/premium' || unlocalizedPathname.startsWith('/premium/');
}

export function getXsollaPremiumEligibilityPath(pathname: string): string {
  return setPathLocale('/premium', getPathLocale(pathname) || 'en');
}

function getPublicPremiumAffinityMarketCountry(
  pathname: string,
  config?: TXsollaRolloutConfig | null,
  claimedCharacterRegions?: string[] | null
): string | null {
  if (!isXsollaPremiumPath(pathname)) {
    return null;
  }

  const pathLocale = getXsollaPathLocale(pathname);
  const localeMatchedMarket = pathLocale
    ? Object.entries(config?.markets || {}).find(
        ([, market]) => market.publiclyEnabled && market.siteLocales.includes(pathLocale)
      )
    : undefined;
  if (localeMatchedMarket) {
    return localeMatchedMarket[0];
  }

  for (const region of claimedCharacterRegions || []) {
    const normalizedRegion = normalizeCountryCode(region);
    if (normalizedRegion && getMarketConfig(normalizedRegion, config)?.publiclyEnabled) {
      return normalizedRegion;
    }
  }

  return null;
}

function getVisibleCheckoutMarketCountry(
  pathname: string,
  config?: TXsollaRolloutConfig | null,
  countryCode?: string | null,
  isInternalTester?: boolean,
  claimedCharacterRegions?: string[] | null
): string | null {
  const trustedCountryCode = getXsollaResolvedCountry(config, countryCode);

  if (config?.bypassMarketGating || isInternalTester) {
    return pickBypassMarketGatingMarket(pathname, config, trustedCountryCode);
  }

  const trustedMarket = getMarketConfig(trustedCountryCode, config);
  const affinityMarketCountry = getPublicPremiumAffinityMarketCountry(pathname, config, claimedCharacterRegions);

  // Affinity may only move a buyer into the pilot market. Honouring every locale's market lets a
  // buyer who already has a public market of their own open another locale's Premium page and
  // check out in whichever currency the catalog happens to price lowest.
  const allowedAffinityMarketCountry =
    affinityMarketCountry === XSOLLA_PILOT_MARKET_COUNTRY || !trustedMarket?.publiclyEnabled
      ? affinityMarketCountry
      : null;

  // The pilot market's own Premium page is public wherever the request exits. That audience
  // routinely reaches the site through a VPN, so keying its page off the exit node's country
  // drops it back into the Patreon flow at USD prices.
  if (isXsollaPilotLocale(pathname) && allowedAffinityMarketCountry === XSOLLA_PILOT_MARKET_COUNTRY) {
    return allowedAffinityMarketCountry;
  }

  if (!trustedMarket?.publiclyEnabled) {
    return null;
  }

  const publicMarketCountry =
    allowedAffinityMarketCountry || (isPathAllowedForMarket(pathname, trustedMarket) ? trustedCountryCode : null);

  if (!publicMarketCountry) {
    return null;
  }

  return trustedCountryCode === XSOLLA_PILOT_MARKET_COUNTRY || config?.authenticatedRolloutEligible
    ? publicMarketCountry
    : null;
}

/**
 * Whether a client-supplied market may be used for pricing and checkout. Mirrors the affinity rule
 * in `getVisibleCheckoutMarketCountry` so a crafted `market` cannot buy at another market's price.
 */
export function isAllowedXsollaCheckoutMarket(
  requestedMarketCountry: string | null | undefined,
  trustedCountryCode: string | null | undefined,
  isTrustedCountryPublicMarket: boolean
): boolean {
  const normalizedRequestedMarket = normalizeCountryCode(requestedMarketCountry);
  if (!normalizedRequestedMarket) {
    return false;
  }

  return (
    normalizedRequestedMarket === normalizeCountryCode(trustedCountryCode) ||
    normalizedRequestedMarket === XSOLLA_PILOT_MARKET_COUNTRY ||
    !isTrustedCountryPublicMarket
  );
}

export function isXsollaPublicRolloutUser(userId?: number | null, rolloutPercent?: number | null): boolean {
  if (typeof userId !== 'number' || !Number.isSafeInteger(userId) || userId <= 0) {
    return false;
  }
  if (
    typeof rolloutPercent !== 'number' ||
    !Number.isInteger(rolloutPercent) ||
    rolloutPercent <= 0 ||
    rolloutPercent > 100
  ) {
    return false;
  }

  const bucket = ((userId % 100) * XSOLLA_ROLLOUT_BUCKET_MULTIPLIER) % 100;
  return bucket < rolloutPercent;
}

export function getXsollaResolvedCountry(
  config?: TXsollaRolloutConfig | null,
  countryCode?: string | null
): string | null {
  return normalizeCountryCode(config?.resolvedCountry || countryCode);
}

export function isXsollaPubliclyVisible(
  pathname: string,
  config?: TXsollaRolloutConfig | null,
  countryCode?: string | null,
  isInternalTester?: boolean,
  claimedCharacterRegions?: string[] | null
): boolean {
  if (!config?.enabled) {
    return false;
  }

  return !!getVisibleCheckoutMarketCountry(pathname, config, countryCode, isInternalTester, claimedCharacterRegions);
}

export function getXsollaDisplayLocale(pathname: string): string {
  return isXsollaPilotLocale(pathname) ? 'zh-CN' : 'en-US';
}

export function getXsollaLanguage(pathname: string): string {
  const pathLocale = getPathLocale(pathname);
  const siteLanguage = SITE_LANGUAGES.find((entry) => entry.sitePathRoot === pathLocale || entry.i18nextCode === pathLocale);

  if (!siteLanguage) {
    return 'en';
  }

  return siteLanguage.i18nextCode === 'zh' ? 'cn' : siteLanguage.i18nextCode;
}

export function getXsollaMarketCountry(_pathname: string, countryCode?: string | null): string | null {
  return normalizeCountryCode(countryCode);
}

export function resolveXsollaCheckoutExperience(
  pathname: string,
  config?: TXsollaRolloutConfig | null,
  countryCode?: string | null,
  isInternalTester?: boolean,
  claimedCharacterRegions?: string[] | null
): TXsollaCheckoutExperience {
  const trustedCountryCode = getXsollaResolvedCountry(config, countryCode);
  const checkoutMarketCountry = getVisibleCheckoutMarketCountry(
    pathname,
    config,
    trustedCountryCode,
    isInternalTester,
    claimedCharacterRegions
  );
  const marketCountry =
    config?.enabled && checkoutMarketCountry ? checkoutMarketCountry : normalizeCountryCode(trustedCountryCode);
  const market = getMarketConfig(marketCountry, config);

  return {
    isVisible: !!config?.enabled && !!checkoutMarketCountry,
    displayLocale: getXsollaDisplayLocale(pathname),
    language: getXsollaLanguage(pathname),
    marketCountry: getXsollaMarketCountry(pathname, marketCountry),
    sandbox: !!config?.sandbox,
    availablePaymentMethods: market?.availablePaymentMethods || []
  };
}
